<template>
  <div v-if="modelValue">
    <Setting
      v-for="([key, setting], i) in entries.filter(
        ([_, setting]) => setting.visible === undefined || setting.visible
      )"
      :key="setting.header"
      :type="getSettingType(setting.type)"
      :disabled="setting.disabled"
    >
      <template #header>
        <Tooltip.Hint :text="setting.tooltip" :disabled="!setting.tooltip">
          <span
            :class="{
              'border-b border-dotted border-border hover:bg-card':
                setting.tooltip,
            }"
          >
            {{ setting.header }}
          </span>
        </Tooltip.Hint>
      </template>
      <template v-if="setting.description" #description>
        {{ setting.description }}
      </template>

      <template v-if="setting.type === 'switcher'">
        <Switcher v-model="coercedModelValue[key]" :modes="setting.options" />
      </template>
      <Form.SelectInput
        v-else-if="setting.type === 'select'"
        v-model="coercedModelValue[key]"
        :options="setting.options"
        :state="setting.state"
        :disabled="setting.disabled"
      />
      <FontSelect
        v-else-if="setting.type === 'font'"
        v-model="coercedModelValue[key]"
        :options="fontOptions"
        :disabled="setting.disabled"
      />
      <Form.SelectInput
        v-else-if="setting.type === 'display'"
        v-model="coercedModelValue[key]"
        :options="displayOptions"
        :state="setting.state"
        :disabled="setting.disabled"
      />
      <div v-else-if="setting.type === 'multiselect'">
        <div class="flex flex-col items-end gap-2">
          <div
            v-for="option in coercedModelValue[key]"
            :key="option"
            class="flex items-center gap-2 rounded-full border border-border bg-card p-1 pl-3 text-left transition hover:shadow"
          >
            <span class="truncate text-sm text-muted-foreground">{{
              option
            }}</span>
            <button
              type="button"
              class="text-muted-foreground outline-2 outline-blue-500 transition hover:bg-muted focus-visible:outline active:bg-muted"
              @click="removeValue(key, option)"
            >
              <XMarkIcon class="size-4" />
            </button>
          </div>
          <Select
            v-model:selection="coercedModelValue[key]"
            :options="setting.options"
            :disabled="setting.disabled"
          >
            <template #trigger>
              <GhostButton
                icon="plus"
                class="!hover:bg-blue-500 !mx-0 !rounded-full bg-blue-500/50 text-sm text-white"
                >Add</GhostButton
              >
            </template>
          </Select>
        </div>
      </div>
      <CurrencyInput
        v-else-if="setting.type === 'currency'"
        v-model="coercedModelValue[key]"
        :currency="setting.currency"
        :autofocus="i === 0"
        :required="setting.required"
        :disabled="setting.disabled"
      />
      <NumberInput
        v-else-if="setting.type === 'number'"
        v-model="coercedModelValue[key]"
        :autofocus="i === 0"
        :required="setting.required"
        :disabled="setting.disabled"
      />
      <Toggle
        v-else-if="setting.type === 'boolean'"
        v-model="coercedModelValue[key]"
        :label="setting.header"
        :disabled="setting.disabled"
      />
      <MultistringInput
        v-else-if="setting.type === 'multistring'"
        v-model="coercedModelValue[key]"
        :disabled="setting.disabled"
      />
      <TextInput
        v-else-if="setting.type === 'text'"
        v-model="coercedModelValue[key]"
        :autofocus="i === 0"
        :state="setting.state"
        :required="setting.required"
      />
      <TextInput
        v-else-if="setting.type === 'email'"
        v-model="coercedModelValue[key]"
        :autofocus="i === 0"
        type="email"
        :state="setting.state"
        :required="setting.required"
      />
      <LongTextInput
        v-else-if="setting.type === 'longtext'"
        v-model="coercedModelValue[key]"
        :autofocus="i === 0"
        :required="setting.required"
      />
      <UrlInput
        v-else-if="setting.type === 'url'"
        v-model="coercedModelValue[key]"
        :autofocus="i === 0"
        :required="setting.required"
      />
      <ColorPicker
        v-else-if="setting.type === 'color'"
        v-model="coercedModelValue[key]"
      />
      <ImageInput
        v-else-if="setting.type === 'image'"
        v-model="coercedModelValue[key]"
      />
    </Setting>
  </div>
</template>

<script lang="ts" setup generic="Model, C extends Configuration<Model>">
import { computed } from "vue";

import Setting from "@/components/Setting/Setting.vue";
import CurrencyInput from "@/components/Utilities/Forms/CurrencyInput.vue";
import GhostButton from "@/components/Utilities/Forms/GhostButton.vue";
import ImageInput from "@/components/Utilities/Forms/ImageInput.vue";
import LongTextInput from "@/components/Utilities/Forms/LongTextInput.vue";
import MultistringInput from "@/components/Utilities/Forms/MultistringInput.vue";
import NumberInput from "@/components/Utilities/Forms/NumberInput.vue";
import Toggle from "@/components/Utilities/Forms/Toggle.vue";
import UrlInput from "@/components/Utilities/Forms/UrlInput.vue";
import Switcher from "@/components/Utilities/Switcher.vue";
import Form from "@/design_system/Form";
import FontSelect from "@/design_system/Form/FontSelect.vue";
import TextInput, {
  type ValidationStatus,
} from "@/design_system/Form/TextInput.vue";
import Select from "@/design_system/Select";
import Tooltip from "@/design_system/Tooltip";
import ColorPicker from "@/design_system/v2/Form/ColorPicker.vue";
import XMarkIcon from "@/icons/heroicons/x-mark-micro.svg";

/** @knipignore This has to be exported for Configuration type to be exported */
export type Options = Array<{
  id: string;
  label: string;
}>;

export type ConfigurationValue = {
  header: string;
  description?: string;
  tooltip?: string;
  state?: ValidationStatus | null;
  disabled?: boolean;
  visible?: boolean;
  required?: boolean;
} & (
  | { type: "text" }
  | { type: "multistring" }
  | { type: "longtext" }
  | { type: "email" }
  | { type: "url" }
  | { type: "color" }
  | { type: "number" }
  | { type: "currency"; currency: string }
  | { type: "boolean" }
  | {
      type: "switcher";
      options: Options;
    }
  | {
      type: "select";
      options: Options;
    }
  | {
      type: "font";
    }
  | {
      type: "display";
      options: Options;
    }
  | {
      type: "multiselect";
      options: Options;
    }
  | {
      type: "image";
    }
);

export type Configuration<Model> = {
  [K in keyof Model]?: ConfigurationValue &
    (
      | {
          type: Model[K] extends string | (string | undefined) | (string | null)
            ? "text" | "longtext" | "url" | "color" | "email"
            : Model[K] extends number | (number | undefined)
            ? "number" | "currency"
            : "boolean";
        }
      | {
          type: Model[K] extends string | (string | undefined) | (string | null)
            ? "select"
            : "multiselect";
          options: Options;
        }
      | {
          type: Model[K] extends string | (string | undefined) | (string | null)
            ? "font"
            : never;
        }
      | {
          type: Model[K] extends string | (string | undefined) | (string | null)
            ? "display"
            : never;
        }
      | {
          type: Model[K] extends string[] ? "multistring" : never;
        }
      | {
          type: "switcher" | "radio";
          options: Options;
        }
      | {
          type: "image";
        }
    );
};

const props = defineProps<{
  configuration: C;
}>();

const entries = computed(
  () =>
    Object.entries(props.configuration) as [
      keyof {
        [K in keyof Model]?: Model[K] extends string ? string : never;
      },
      ConfigurationValue
    ][]
);

const modelValue = defineModel<Model>({
  required: true,
});

const coercedModelValue = modelValue as any;

const fontOptions: Array<{ id: string; label: string }> = [
  { id: "Aleo", label: "Aleo" },
  { id: "Alegreya Variable", label: "Alegreya" },
  { id: "Cabin Variable", label: "Cabin" },
  { id: "Cambria", label: "Cambria" },
  { id: "EB Garamond Variable", label: "EB Garamond" },
  { id: "Figtree Variable", label: "Figtree" },
  { id: "Georgia", label: "Georgia" },
  { id: "Hex Franklin", label: "Hex Franklin" },
  { id: "IBM Plex Mono", label: "IBM Plex Mono" },
  { id: "Lato", label: "Lato" },
  { id: "Lora Variable", label: "Lora" },
  { id: "Merriweather Variable", label: "Merriweather" },
  { id: "Montserrat Variable", label: "Montserrat" },
  { id: "serif", label: "serif" },
  { id: "Source Serif 4", label: "Source Serif 4" },
  { id: "Times", label: "Times" },
  { id: "Times New Roman", label: "Times New Roman" },
  { id: "ui-serif", label: "ui-serif" },
];

const displayOptions: Options = [
  { id: "block", label: "Block" },
  { id: "none", label: "None" },
];

const getSettingType = (type: ConfigurationValue["type"]) => {
  switch (type) {
    case "color":
    case "multistring":
    case "switcher":
    case "image":
      return "group";

    default:
      return "single";
  }
};

const removeValue = (key: keyof Model, value: string) => {
  modelValue.value[key] = (
    modelValue.value[key] as string[] | undefined
  )?.filter((o) => o !== value) as Model[keyof Model];
};
</script>
